import {
    Telescope,
    HeartHandshake,
    ShieldCheck,
    Eye,
    Lightbulb,
    TrendingUp,
    Linkedin,
    Info,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { LEADERS } from "@/data/leadership";

const PRINCIPLES = [
    { icon: Telescope, title: "Long-Term Thinking", desc: "We build for durability — products and infrastructure meant to last, not short-term wins." },
    { icon: HeartHandshake, title: "Customer First", desc: "Every decision is measured against the value and clarity it gives our users." },
    { icon: ShieldCheck, title: "Security by Design", desc: "Security is built into the exchange flow from the start, not added afterwards." },
    { icon: Eye, title: "Transparency", desc: "Clear rates, clear processes and honest communication — internally and externally." },
    { icon: Lightbulb, title: "Continuous Innovation", desc: "We keep improving the exchange experience, from live rates to payout speed." },
    { icon: TrendingUp, title: "Responsible Growth", desc: "We scale deliberately, with risk awareness and sustainable foundations." },
];

function getInitials(name) {
    return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
}

export default function Leadership() {
    usePageMeta({
        title: "DtOrbit Leadership | Team & Vision",
        description:
            "Meet the leadership team and explore the vision, expertise and principles driving DtOrbit.",
        path: "/leadership",
    });

    return (
        <>
            <PageHero
                testid="leadership-hero"
                label="Leadership"
                title="Leadership"
                description="Building the vision, technology and products behind DtOrbit."
            />

            <section data-testid="leadership-intro" className="relative py-10 md:py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="max-w-3xl text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                            DtOrbit's leadership brings together technology,
                            product innovation, customer experience, security,
                            responsible growth and a long-term vision. This
                            multidisciplinary approach guides the platform's
                            exchange experience, infrastructure and direction.
                        </p>
                    </Reveal>

                    <Reveal delay={120}>
                        <div
                            data-testid="leadership-statement"
                            className="relative mt-10 overflow-hidden rounded-3xl glass p-8 md:p-12"
                        >
                            <svg
                                className="pointer-events-none absolute -right-20 -top-24 w-[420px] h-[420px] opacity-60"
                                viewBox="0 0 400 400"
                                fill="none"
                                aria-hidden="true"
                            >
                                <circle cx="200" cy="200" r="180" stroke="#1E56FF" strokeOpacity="0.14" strokeWidth="1.5" />
                                <circle cx="200" cy="200" r="130" stroke="#1E56FF" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="4 8" />
                                <circle cx="200" cy="200" r="80" stroke="#3EC6F0" strokeOpacity="0.25" strokeWidth="1.5" />
                                <circle cx="200" cy="70" r="8" fill="#3EC6F0" fillOpacity="0.7" />
                            </svg>
                            <div className="relative max-w-2xl">
                                <span className="text-xs uppercase tracking-[0.25em] text-[#1E56FF] font-bold">
                                    Our Approach
                                </span>
                                <h2 className="font-display mt-3 text-2xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                                    Building with a long-term vision
                                </h2>
                                <p className="mt-4 text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                                    DtOrbit focuses on building reliable digital
                                    financial products with an emphasis on user
                                    experience, technology, transparency and
                                    responsible growth. Our leadership approach
                                    is simple: do a few things exceptionally
                                    well, earn trust through consistency, and
                                    scale only when the foundation is strong.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section data-testid="leadership-principles" className="relative py-12 md:py-16 border-t border-[#E4EAF6] bg-white/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Our Leadership Principles
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {PRINCIPLES.map(({ icon: Icon, title, desc }, i) => (
                            <Reveal key={title} delay={(i % 3) * 100}>
                                <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`principle-${i + 1}`}>
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                        <Icon className="w-5 h-5 text-[#1E56FF]" />
                                    </span>
                                    <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                        {desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="leadership-grid" className="relative py-12 md:py-16 border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Leadership Team
                        </h2>
                    </Reveal>
                    <Reveal delay={80}>
                        <div
                            data-testid="leadership-placeholder-note"
                            className="mt-6 glass rounded-2xl p-5 flex items-start gap-3 max-w-3xl"
                        >
                            <Info className="w-5 h-5 text-[#1E56FF] flex-none mt-0.5" />
                            <p className="text-sm text-[#5B6B8C] leading-relaxed">
                                Demo content: the profiles below use placeholder
                                names and details for layout purposes only — they
                                do not represent actual DtOrbit employees.
                                Official team information will be published here
                                soon.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {LEADERS.map((leader, i) => (
                            <Reveal key={leader.id} delay={(i % 3) * 90}>
                                <article
                                    data-testid={`leader-card-${leader.id}`}
                                    className="glass card-hover rounded-2xl overflow-hidden h-full flex flex-col"
                                >
                                    <div className="relative aspect-[4/3]">
                                        {leader.photo ? (
                                            <img
                                                src={leader.photo}
                                                alt={`${leader.name}, ${leader.role} at DtOrbit`}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#EAF2FF] via-[#F0F7FF] to-[#D9F3FD]">
                                                <span className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_10px_26px_-8px_rgba(30,86,255,0.55)] font-display text-2xl font-extrabold text-white tracking-wide">
                                                    {getInitials(leader.name)}
                                                </span>
                                                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8494B0]">
                                                    Photo Placeholder
                                                </span>
                                            </div>
                                        )}
                                        {leader.placeholder && (
                                            <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#1E56FF] border border-[#D5E4FF]">
                                                Demo Profile
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-5 flex flex-col flex-1">
                                        <h3 className="font-display text-base font-bold text-[#0F1B3D]">
                                            {leader.name}
                                        </h3>
                                        <p className="mt-0.5 text-xs font-bold text-[#1E56FF] uppercase tracking-wide">
                                            {leader.role}
                                        </p>
                                        <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed flex-1">
                                            {leader.bio}
                                        </p>
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {leader.expertise.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-2.5 py-1 text-[10px] font-semibold text-[#1E56FF]"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-[#EDF2FB] flex items-center justify-between">
                                            <span className="text-[11px] font-semibold text-[#8494B0]">
                                                Full profile coming soon
                                            </span>
                                            {leader.linkedin && (
                                                <a
                                                    href={leader.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`leader-linkedin-${leader.id}`}
                                                    aria-label={`${leader.name} on LinkedIn`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#EAF2FF] border border-[#D5E4FF] text-[#1E56FF] transition-colors hover:bg-[#1E56FF] hover:text-white"
                                                >
                                                    <Linkedin className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
